/**
 * Organizer-authored rich text (Quill/CKEditor, per CLAUDE.md) often carries inline styles and
 * wrapper divs from a Word/Outlook paste - e.g. `background-color: rgb(255,255,255)`,
 * `font-family: "Segoe UI Historic"` on nested divs (a real example: event 16498's "1/10 2wd
 * Nitro Stadium Truck" class rules). Rendered as-authored, those inline styles fight the page's
 * own design system and can visually read as separate little boxes stacked inside one card. This
 * strips presentational attributes so the content always inherits our typography/color, keeping
 * only real structure (paragraphs, bold, links, line breaks).
 *
 * Also strips script tags, inline event handlers, and javascript: URLs - the legacy CFML page
 * trusts this content completely unescaped, so this isn't fixing a regression, but it's a cheap,
 * genuine improvement to make while already parsing the markup, and organizer/track accounts
 * aren't a trust boundary worth skipping easy defense-in-depth for.
 */
export function sanitizeRichText(html: string): string {
  if (!html) return ''
  if (typeof DOMParser === 'undefined') return html

  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')

    doc.querySelectorAll('script, style').forEach((el) => el.remove())

    doc.body.querySelectorAll('*').forEach((el) => {
      el.removeAttribute('style')
      el.removeAttribute('class')
      el.removeAttribute('id')
      for (const attr of Array.from(el.attributes)) {
        if (attr.name.toLowerCase().startsWith('on')) el.removeAttribute(attr.name)
      }
      if (el.tagName === 'A') {
        const href = el.getAttribute('href') || ''
        if (href.trim().toLowerCase().startsWith('javascript:')) el.removeAttribute('href')
      }
    })

    return doc.body.innerHTML
  } catch {
    return html
  }
}
